import { FileSpreadsheet, Inbox, LoaderCircle, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ComplaintCards } from "@/components/admin/ComplaintCards";
import { ComplaintsTable } from "@/components/admin/ComplaintsTable";
import { FiltersBar } from "@/components/admin/FiltersBar";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const EmptyState = ({ filtered }) => (
  <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center" data-testid="complaints-empty">
    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><Inbox className="h-6 w-6" /></span>
    <p className="font-heading font-semibold text-slate-800">{filtered ? "Tidak ada pengaduan yang cocok" : "Belum ada pengaduan masuk"}</p>
    <p className="text-sm text-slate-500">{filtered ? "Coba ubah atau reset filter." : "Pengaduan dari warga akan tampil di sini."}</p>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center" data-testid="complaints-error">
    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500"><WifiOff className="h-6 w-6" /></span>
    <p className="font-heading font-semibold text-slate-800">Data pengaduan gagal dimuat</p>
    <p className="text-sm text-slate-500">Koneksi ke server sedang terganggu. Silakan coba lagi.</p>
    <Button onClick={onRetry} className="mt-2 rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="complaints-retry-btn">
      <RefreshCw className="h-4 w-4" /> Coba Lagi
    </Button>
  </div>
);

export const ComplaintsSection = ({ complaints, loading, filters, onFiltersChange, onExport, exporting, loadError, onRetry, ...handlers }) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const filtered = filters.jenis !== "all" || filters.status !== "all" || !!filters.q || !!filters.from;
  const List = isDesktop ? ComplaintsTable : ComplaintCards;
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="complaints-section">
      <div className="space-y-4 border-b border-slate-100 p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            Data Pengaduan {loading && <LoaderCircle className="h-4 w-4 animate-spin text-emerald-600" />}
          </h3>
          <Button
            variant="outline"
            onClick={onExport}
            disabled={exporting || loading || !complaints.length}
            className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
            data-testid="export-excel-btn"
          >
            {exporting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Unduh Excel
          </Button>
        </div>
        <FiltersBar filters={filters} onChange={onFiltersChange} count={complaints.length} />
      </div>
      {loading && !complaints.length ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500" data-testid="complaints-loading">
          <LoaderCircle className="h-5 w-5 animate-spin text-emerald-600" /> Memuat data pengaduan...
        </div>
      ) : loadError && !complaints.length ? (
        <ErrorState onRetry={onRetry} />
      ) : complaints.length === 0 ? (
        <EmptyState filtered={filtered} />
      ) : (
        <List complaints={complaints} {...handlers} />
      )}
    </section>
  );
};
